<template>
  <template v-if="isTabbed">
    <div class="consultation-template-tabbed">
      <MTabs v-model="activeTab" scroll class="consultation-template-tabbed__tabs">
        <MTab
          v-for="(tab, index) in props.tabs"
          :key="'tab-panel-' + index"
          :name="String(index)"
          :label="tab.title"
        >
          <MLayoutStack gap="4">
            <MConsultationTemplateFormElement
              v-for="(item, elementIndex) in tab.elements"
              :key="'tab-' + index + '-element-' + elementIndex"
              :element="item"
              :previous-element="elementIndex > 0 ? tab.elements[elementIndex - 1] : undefined"
              :form-values="formValues"
              :patient-id="props.patientId"
              :consultation-topic-id="props.consultationTopicId"
              @form-value-update="handleFormValueUpdate"
              @field-focused="(p) => emit('fieldFocused', p)"
              @field-blurred="(p) => emit('fieldBlurred', p)"
            />
          </MLayoutStack>
        </MTab>
      </MTabs>

      <div class="consultation-template-tabbed__footer flex justify-between items-center gap-2">
        <MButton
          label="Previous"
          color="secondary"
          :disabled="activeIndex === 0"
          @click="goToPreviousTab"
        />
        <div class="flex items-center gap-2">
          <MButton
            label="Save & close"
            :color="isLastTab ? 'primary' : 'secondary'"
            @click="emit('requestSubmit')"
          />
          <MButton v-if="!isLastTab" :label="`Next: ${nextTabTitle}`" @click="goToNextTab" />
        </div>
      </div>
    </div>
  </template>

  <template v-else>
    <MLayoutStack gap="4">
      <MConsultationTemplateFormElement
        v-for="(item, index) in props.results"
        :key="'consultationTemplateFormElement' + index"
        :element="item"
        :previous-element="index > 0 ? props.results[index - 1] : undefined"
        :form-values="formValues"
        :patient-id="props.patientId"
        :consultation-topic-id="props.consultationTopicId"
        @form-value-update="handleFormValueUpdate"
        @field-focused="(p) => emit('fieldFocused', p)"
        @field-blurred="(p) => emit('fieldBlurred', p)"
      />
    </MLayoutStack>
  </template>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import MConsultationTemplateFormElement from "../MConsultationTemplateFormElement/MConsultationTemplateFormElement.vue";
import MTabs from "../../MTabs";
import MTab from "../../MTab";
import MButton from "../../MButton";
import MLayoutStack from "../../MLayoutStack";
import { useDebouncedThrottledFunction } from "../../../../composables/throttleDebounce";

const props = defineProps({
  results: Array,
  formValues: Object,
  patientId: String,
  consultationTopicId: String,
  tabs: { type: Array as () => { title: string; elements: unknown[] }[] | null, default: null },
});

// Multi-form templates render as tabs (native MTabs panels, so the modal's built-in
// `.drawer-modal.tab` layout handles the fixed header + scrollable content); single-form
// templates keep the existing flat layout. Switching tabs triggers an explicit save
// (flushPendingSave), so no in-progress data is lost across the panel remount.
const isTabbed = computed(() => Array.isArray(props.tabs) && props.tabs.length > 0);
const activeTab = ref("0");
const activeIndex = computed(() => Number(activeTab.value));
const isLastTab = computed(() => activeIndex.value === (props.tabs?.length ?? 0) - 1);
const nextTabTitle = computed(() => props.tabs?.[activeIndex.value + 1]?.title ?? "");

function goToPreviousTab() {
  if (activeIndex.value > 0) {
    flushPendingSave();
    activeTab.value = String(activeIndex.value - 1);
  }
}

function goToNextTab() {
  if (!isLastTab.value) {
    flushPendingSave();
    activeTab.value = String(activeIndex.value + 1);
  }
}

type FieldPayload = { modelValueKey: string; value: unknown } | undefined;

const emit = defineEmits<{
  (e: "formUpdatedSave", payload?: FieldPayload): void;
  (e: "fieldFocused", payload: { modelValueKey: string; value: unknown }): void;
  (e: "fieldBlurred", payload: { modelValueKey: string; value: unknown }): void;
  (e: "requestSubmit"): void;
}>();

let lastPayload: FieldPayload = undefined;

async function saveForm() {
  emit("formUpdatedSave", lastPayload);
  lastPayload = undefined;
}

function handleFormValueUpdate(payload?: FieldPayload) {
  if (payload) lastPayload = payload;
  debouncedThrottledSaveEntries();
}

// Switching tabs saves immediately: cancel the queued debounce and flush the current change
// so nothing is lost mid-navigation (the composable has no flush, only cancel).
function flushPendingSave() {
  cancelDebouncedThrottled();
  saveForm();
}

const { debouncedThrottled: debouncedThrottledSaveEntries, cancelDebouncedThrottled } =
  useDebouncedThrottledFunction(() => saveForm(), 500, 800);
</script>
<style scoped>
/* Own layout only — the fixed-header / scrollable-panel / width-bounding is owned by the
   modal shells' `.tab` type (drawer `Modal.vue` and docked `MSplitPageModal`). */
.consultation-template-tabbed {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  height: 100%;
}

.consultation-template-tabbed__tabs.m-tabs {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
}

.consultation-template-tabbed__footer {
  flex: 0 0 auto;
}

/* Match the gap-4 field spacing used elsewhere in this form — scoped here rather than
   in MSection itself, which is shared far beyond this component. Every MSection in this
   tree sits inside an MLayoutStack, so qualifying on that ancestor class (rather than
   just `.m-section`) outweighs MSection's own same-specificity scoped rule without
   relying on source order or `!important`. */
:deep(.m-layout-stack .m-section) {
  gap: var(--gap-4);
}

:deep(.m-layout-stack .m-section + .m-section) {
  margin-top: var(--gap-4);
}
</style>
